import React from 'react'
import { Dimensions } from 'react-native'

import { BottomSheetModalProvider } from '@gorhom/bottom-sheet'
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister'
import { QueryClient } from '@tanstack/react-query'
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client'
import { ToastProvider } from '@zet/common/components/toast'
import storageUtil from '@zet/utils/storage'
import { GestureHandlerRootView } from 'react-native-gesture-handler'
import { createModalStack, ModalProvider } from 'react-native-modalfy'
import { SafeAreaProvider, SafeAreaView } from 'react-native-safe-area-context'

import modalConfig from '../modals/modalConfig'

const stack = createModalStack(modalConfig.config, modalConfig.defaultOptions)

const clientStorage = {
  setItem: (key: string, value: any) => {
    storageUtil.mmkvStorage.set(key, value)
  },
  getItem: (key: string) => {
    const value = storageUtil.mmkvStorage.getString(key)
    return value === undefined ? null : value
  },
  removeItem: (key: string) => {
    storageUtil.mmkvStorage.delete(key)
  },
}

const persister = createSyncStoragePersister({ storage: clientStorage })

export const AppProviders = ({
  children,
  onLayout,
  queryClient,
}: {
  children: React.ReactNode
  onLayout: () => void
  queryClient: QueryClient
}) => {
  const { height, width } = Dimensions.get('window')

  const isLandscape = width > height

  const containerStyle = {
    ...(isLandscape && {
      maxWidth: '640px',
    }),
  }

  return (
    <GestureHandlerRootView className="flex-1" onLayout={onLayout}>
      <PersistQueryClientProvider
        client={queryClient}
        persistOptions={{
          persister,
          dehydrateOptions: {
            shouldDehydrateQuery: (query) => {
              const queryIsReadyForPersistance =
                query.state.status === 'success'
              if (queryIsReadyForPersistance) {
                const { queryKey } = query
                const excludeFromPersisting = queryKey.includes('cc/brands')
                return !excludeFromPersisting
              }
              return queryIsReadyForPersistance
            },
          },
        }}
      >
        <SafeAreaProvider className="bg-grays-20 flex-1 items-center">
          <SafeAreaView
            className="w-full flex-1 bg-white"
            style={containerStyle}
          >
            <ToastProvider>
              <ModalProvider stack={stack}>
                <BottomSheetModalProvider>{children}</BottomSheetModalProvider>
              </ModalProvider>
            </ToastProvider>
          </SafeAreaView>
        </SafeAreaProvider>
      </PersistQueryClientProvider>
    </GestureHandlerRootView>
  )
}
